import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useCrm } from "@/hooks/useCrm";
import { getAction } from "@/lib/actions";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/dates";
import { SCHEMA, type FieldDef } from "@/lib/schema";
import type { FieldValue, HistoryEntry, Page, TableKey } from "@/lib/types";

// Historiek van één record (contract §6.4b, B55): wie wijzigde wat, via welke actie of Bewerken.
// Inklapbaar onderaan Bewerken; laadt pas bij openen. Alleen met het recht audit.lezen.
// HistoryPanel is de algemene vorm (ook voor kaarten op eigen borden, §6.9).

export function RecordHistory({ table, id }: { table: TableKey; id: string }) {
  const crm = useCrm();
  const def = SCHEMA.tables[table];
  const show = (name: string, value: FieldValue | null | undefined) => {
    const field: FieldDef | undefined = def.fields.find((f) => f.name === name);
    if (value === null || value === undefined || value === "") return "—";
    if (field?.type === "link") return (Array.isArray(value) ? value : []).map((v) => crm.name(field.table!, v) || v).join(", ") || "—";
    if (field?.type === "datetime") return fmtDateTime(value);
    if (field?.type === "date") return fmtDate(value);
    if (field?.type === "currency") return fmtMoney(value);
    return String(value);
  };
  return (
    <HistoryPanel
      path={`records/${table}/${id}/history`}
      fields={def.fields.map((f) => f.name)}
      show={show}
      actionLabel={(action) => getAction(action)?.label ?? action}
      emptyText="Nog geen wijzigingen geregistreerd (de historiek bestaat sinds fase 3b)."
    />
  );
}

export function HistoryPanel({
  path,
  fields,
  show,
  actionLabel,
  emptyText = "Nog geen wijzigingen geregistreerd."
}: {
  /** API-pad zonder /api/, bv. records/properties/recP01/history. */
  path: string;
  /** Veldnamen in de volgorde waarin ze getoond worden. */
  fields: string[];
  show: (name: string, value: FieldValue | null | undefined) => string;
  actionLabel: (action: string) => string;
  emptyText?: string;
}) {
  const crm = useCrm();
  const [page, setPage] = useState<Page<HistoryEntry> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!crm.can("audit.lezen")) return null;

  const load = async (cursor?: string) => {
    setBusy(true);
    try {
      const next = await api<Page<HistoryEntry>>(`${path}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`);
      setPage((current) => (cursor && current ? { items: [...current.items, ...next.items], nextCursor: next.nextCursor } : next));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const order = (name: string) => {
    const i = fields.indexOf(name);
    return i === -1 ? 999 : i;
  };

  return (
    <details className="mt-4 rounded-[10px] border bg-surface-soft px-3 py-2" onToggle={(e) => (e.currentTarget.open && !page && !busy ? void load() : undefined)}>
      <summary className="cursor-pointer text-xs font-bold text-navy-800">Historiek</summary>
      <div className="mt-2 text-xs">
        {error && <p className="text-tone-red">Historiek kon niet geladen worden: {error}</p>}
        {!page && !error && <p className="text-muted-foreground">Laden…</p>}
        {page && !page.items.length && <p className="text-muted-foreground">{emptyText}</p>}
        {page && page.items.length > 0 && (
          <ul className="grid gap-2">
            {page.items.map((entry) => {
              const source = entry.source === "action" ? `Actie: ${actionLabel(entry.action ?? "")}` : entry.source === "system" ? "Systeem" : "Bewerken";
              return (
                <li key={entry.id} className="rounded-lg bg-white px-2.5 py-2">
                  <div>
                    <strong>{fmtDateTime(entry.at)}</strong> · {entry.actor?.name ?? (entry.source === "system" ? "FurniFlats CRM" : "onbekend")} · {source} · {entry.operation === "create" ? "aangemaakt" : "gewijzigd"}
                  </div>
                  <ul className="mt-1 grid gap-0.5 text-muted-foreground">
                    {Object.entries(entry.changes)
                      .sort(([a], [b]) => order(a) - order(b))
                      .map(([name, change]) => (
                        <li key={name}>
                          <strong className="text-foreground">{name}</strong>: {entry.operation === "create" ? "" : `${show(name, change.from)} → `}
                          {show(name, change.to)}
                        </li>
                      ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
        {page?.nextCursor && (
          <Button size="sm" variant="ghost" className="mt-2" disabled={busy} onClick={() => void load(page.nextCursor!)}>
            Meer laden
          </Button>
        )}
      </div>
    </details>
  );
}
